import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import * as THREE from 'three';
import useEditorStore from './useEditorStore';

/**
 * Factory for a sculpt brush's per-step store.
 *
 * Two levels of sharing are going on here, and they solve different problems.
 *
 * **Per step.** A brush that appears on more than one step needs its *own*
 * state on each. Sharing one store would make an apply on one step show as
 * applied on the other — the card would offer an Undo for an operation that
 * belongs somewhere else. The smoothening tool solved that by copying its store
 * file once per step, which is why there are four near-identical
 * `*SmootheningStore.js` files that have to be edited in lockstep. A factory
 * gives the same isolation from one definition.
 *
 * **Per brush.** Inflate and Sculpt Smooth differ only in what their `brush`
 * object holds and what the applied mesh is called. Everything else — the
 * session mirror, the apply/undo command, the reset — is the same code, so it is
 * written once here and configured, rather than copied per brush.
 *
 * @param {Object} options
 * @param {string} options.name - devtools store name; must be unique per instance
 * @param {Object} options.brushDefaults - the brush's full default settings object
 * **Repeat applies.** A brush is not a one-shot operation: a clinician sculpts,
 * commits, then keeps going on the committed surface. Each apply therefore
 * stacks a command of its own rather than latching the tool shut, and `isApplied`
 * means "at least one committed apply is still undoable" — not "this tool is
 * finished". Undo unwinds the applies newest-first, which is the same one-step
 * model the pre-apply snapshot pool uses, so the live and post-reload undo paths
 * agree on what a click takes back.
 *
 * @param {string} options.appliedMeshName - scene-graph name for the committed mesh
 * @returns {import('zustand').UseBoundStore<any>}
 */
export function createSculptBrushStore({ name, brushDefaults, appliedMeshName }) {
  const createDefaultBrush = () => ({ ...brushDefaults });

  return create(
    devtools(
      (set, get) => ({
        brush: createDefaultBrush(),
        setBrush: (data) => set({ brush: { ...get().brush, ...data } }),

        /** The mesh being sculpted. Owned by the handler; mirrored here for the apply command. */
        workingMesh: null,
        setWorkingMesh: (mesh) => set({ workingMesh: mesh }),

        /** Bumped after every committed stroke so the card can react. */
        strokeCount: 0,
        /** Undoable strokes currently held by the live session. */
        undoDepth: 0,
        setStrokeState: ({ strokeCount, undoDepth }) =>
          set((state) => ({
            strokeCount: strokeCount ?? state.strokeCount,
            undoDepth: undoDepth ?? state.undoDepth,
          })),

        /**
         * Handler-provided stroke controls. The card drives undo/reset through
         * these rather than reaching into the scene, keeping the card a pure view.
         * @type {{ undoStroke: () => boolean, resetAll: () => boolean } | null}
         */
        sessionControls: null,
        setSessionControls: (controls) => set({ sessionControls: controls }),

        /**
         * Committed applies still holding undo material, oldest first. The card
         * reads `isApplied`; this is what keeps that flag honest across repeat
         * applies — undoing the newest one leaves the earlier ones standing.
         * @type {Array<{ execute: () => void, undo: () => void }>}
         */
        appliedCommands: [],

        isApplied: false,
        setIsApplied: (status) => set({ isApplied: status, ...(status ? {} : { appliedCommands: [] }) }),
        isAppliedFromDraft: false,
        setIsAppliedFromDraft: (status) => set({ isAppliedFromDraft: status }),

        currentCommand: null,
        setCurrentCommand: (command) => set({ currentCommand: command }),

        /**
         * Build the undoable apply command.
         *
         * Mirrors `createSmootheningCommand`: snapshot the socket as it stands,
         * hand the sculpted geometry to `replaceShadedMesh`, and keep enough
         * state in the closure to put the original back.
         *
         * The geometry is cloned rather than handed over directly because the
         * handler still owns `workingMesh` and disposes it on unmount — the
         * command has to outlive that.
         */
        createSculptCommand: () => {
          const { workingMesh, brush } = get();
          const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();

          if (!workingMesh?.isMesh || !sceneRefs?.shadedMesh) return null;

          const originalMesh = sceneRefs.shadedMesh;
          const originalGeometry = originalMesh.geometry;
          const originalMaterial = sceneRefs.material;
          const originalMatrix = originalMesh.matrix.clone();
          const originalBrush = { ...brush };

          const sculptedGeometry = new THREE.BufferGeometry().copy(workingMesh.geometry);
          const sculptedMatrix = new THREE.Matrix4().copy(workingMesh.matrix);

          const command = {
            execute: () => {
              // The handler's working mesh has served its purpose; drop it so
              // the sculpted copy below is the only thing left in the scene.
              const live = get().workingMesh;
              if (live?.isMesh) {
                live.parent?.remove(live);
                live.geometry?.dispose?.();
              }

              const sculptedMesh = new THREE.Mesh(sculptedGeometry, originalMaterial);
              sculptedMesh.matrix.copy(sculptedMatrix);
              sculptedMesh.matrix.decompose(
                sculptedMesh.position,
                sculptedMesh.quaternion,
                sculptedMesh.scale,
              );
              sculptedMesh.matrixAutoUpdate = true;
              sculptedMesh.name = appliedMeshName;
              replaceShadedMesh(sculptedMesh);

              set((state) => ({
                appliedCommands: [...state.appliedCommands, command],
                isApplied: true,
                // The handler builds a fresh working copy from the socket this
                // apply just installed, so the user can carry on sculpting.
                workingMesh: null,
                sessionControls: null,
                strokeCount: 0,
                undoDepth: 0,
              }));
            },

            undo: () => {
              const restoredMesh = new THREE.Mesh(originalGeometry, originalMaterial);
              restoredMesh.matrix.copy(originalMatrix);
              restoredMesh.matrix.decompose(
                restoredMesh.position,
                restoredMesh.quaternion,
                restoredMesh.scale,
              );
              restoredMesh.matrixAutoUpdate = true;
              replaceShadedMesh(restoredMesh);

              // The strokes themselves are not recoverable — they lived in the
              // session that the apply tore down — so the tool comes back ready
              // to sculpt again rather than half-restored.
              set((state) => {
                // Only this apply is unwound. Any earlier apply is still baked
                // into the socket, so the card must keep offering its Undo.
                const appliedCommands = state.appliedCommands.filter((c) => c !== command);
                return {
                  appliedCommands,
                  isApplied: appliedCommands.length > 0,
                  currentCommand: appliedCommands[appliedCommands.length - 1] ?? null,
                  brush: originalBrush,
                  workingMesh: null,
                  sessionControls: null,
                  strokeCount: 0,
                  undoDepth: 0,
                };
              });
            },
          };

          return command;
        },

        reset: () =>
          set({
            brush: createDefaultBrush(),
            workingMesh: null,
            sessionControls: null,
            strokeCount: 0,
            undoDepth: 0,
            isApplied: false,
            isAppliedFromDraft: false,
            currentCommand: null,
            appliedCommands: [],
          }),
      }),
      { name },
    ),
  );
}

export default createSculptBrushStore;
